import { motion } from "motion/react";
import { Link } from "react-router-dom";
import type { BidFormData } from "./BidForm";

interface CostBreakdown {
  repairCost: number;
  evictionCost: number;
  acquisitionTax: number;
  financeCost: number;
  mgmtFee: number;
  totalCost: number;
}

interface BidResults {
  safeBid: number;
  recommendedBid: number;
  aggressiveBid: number;
  pMax: number;
  costs: CostBreakdown;
  estimatedROI: number;
  winningRateAdjustedPrice: number;
}

/** 연식 가중치 계산 */
function getAgeWeight(age: number): number {
  if (age <= 5) return 0.5;
  if (age <= 10) return 1.0;
  if (age <= 20) return 1.5;
  return 2.0;
}

/** 점유자 유형별 명도 비용 추정 */
function getEvictionCost(area: number, occupantType: string): number {
  switch (occupantType) {
    case "debtor":
      return Math.max(2000000, area * 30000);
    case "tenant_no_rights":
      return Math.max(3000000, area * 50000);
    case "tenant_with_rights":
      return Math.max(10000000, area * 120000);
    case "lien":
      return Math.max(15000000, area * 150000);
    default:
      return 0;
  }
}

/** 보유 주택 수별 취득세율 */
function getAcquisitionTaxRate(homeCount: number, price: number): number {
  if (homeCount <= 1) {
    if (price <= 600000000) return 0.011;   // 1.1%
    if (price <= 900000000) return 0.013;   // 1.3%
    return 0.035;                            // 3.5%
  }
  if (homeCount === 2) return 0.08;          // 8%
  return 0.12;                                // 12%
}

export function calculateBid(data: BidFormData): BidResults {
  const { exclusiveArea, buildingAge, estimatedMarketPrice, regionalWinningRate,
    takeoverAmount, occupantType, homeCount, loanAmount, loanRate, loanDuration,
    unpaidMgmtFee, targetProfit } = data;

  // 수리비: 전용면적 × 50,000 × 연식가중치
  const repairCost = Math.round(exclusiveArea * 50000 * getAgeWeight(buildingAge));

  // 명도비
  const evictionCost = getEvictionCost(exclusiveArea, occupantType);

  // 취득세 (감정가 대신 추정매각시세 기준)
  const taxRate = getAcquisitionTaxRate(homeCount, estimatedMarketPrice);
  const acquisitionTax = Math.round(estimatedMarketPrice * taxRate);

  // 금융비용 (대출 이자)
  const financeCost = Math.round(loanAmount * (loanRate / 100) * (loanDuration / 12));

  // 미납 관리비
  const mgmtFee = unpaidMgmtFee > 0 ? unpaidMgmtFee : Math.round(exclusiveArea * 20000);

  // 총 부대비용
  const totalCost = repairCost + evictionCost + acquisitionTax + financeCost + mgmtFee;

  // P_max = V_est - (R + 총부대비용 + M_target)
  const pMax = estimatedMarketPrice - takeoverAmount - totalCost - targetProfit;

  // 지역 낙찰가율 반영 가격
  const winningRateAdjustedPrice = Math.round(estimatedMarketPrice * (regionalWinningRate / 100));

  // 보수적 입찰가 (Safe Bid): P_max × 0.95
  const safeBid = Math.round(pMax * 0.95);

  // 적정 추천가 (Recommended): min(P_max, V_est × 지역 낙찰가율)
  const recommendedBid = Math.min(pMax, winningRateAdjustedPrice);

  // 공격적 입찰가 (Aggressive): P_max
  const aggressiveBid = pMax;

  // 예상 ROI (적정 추천가 기준)
  const estimatedROI = recommendedBid > 0
    ? Math.round(((estimatedMarketPrice - takeoverAmount - totalCost - recommendedBid) / recommendedBid) * 100)
    : 0;

  return {
    safeBid,
    recommendedBid,
    aggressiveBid,
    pMax,
    costs: { repairCost, evictionCost, acquisitionTax, financeCost, mgmtFee, totalCost },
    estimatedROI,
    winningRateAdjustedPrice,
  };
}

const tierConfig = [
  {
    key: "safe",
    label: "보수적 입찰가",
    sub: "Safe Bid",
    description: "안전마진 극대화 — 낙찰 확률은 낮으나 수익률을 최대한 높입니다",
    icon: "ri-shield-check-line",
    bg: "bg-emerald-50 border-emerald-200",
    badgeBg: "bg-emerald-100 text-emerald-700",
    iconBg: "bg-emerald-500",
  },
  {
    key: "recommended",
    label: "적정 추천가",
    sub: "Recommended Bid",
    description: "낙찰 가능성과 수익성의 최적 밸런스 — 강스터디 추천 입찰가입니다",
    icon: "ri-star-line",
    bg: "bg-[#C4956A]/5 border-[#C4956A]/30",
    badgeBg: "bg-[#C4956A]/10 text-[#C4956A]",
    iconBg: "bg-[#C4956A]",
  },
  {
    key: "aggressive",
    label: "공격적 입찰가",
    sub: "Aggressive Bid",
    description: "수익성 한계선까지 투찰 — 낙찰 확률 최대화, 수익은 최소화",
    icon: "ri-rocket-line",
    bg: "bg-rose-50 border-rose-200",
    badgeBg: "bg-rose-100 text-rose-700",
    iconBg: "bg-rose-500",
  },
];

function formatKRW(n: number): string {
  if (n < 0) return "-" + Math.abs(n).toLocaleString() + "원";
  return n.toLocaleString() + "원";
}

function formatPercent(n: number): string {
  return n > 0 ? "+" + n + "%" : n + "%";
}

interface BidResultProps {
  results: BidResults;
  data: BidFormData;
}

export default function BidResult({ results, data }: BidResultProps) {
  const { costs, pMax, safeBid, recommendedBid, aggressiveBid, estimatedROI, winningRateAdjustedPrice } = results;

  const getBidValue = (key: string) => {
    switch (key) {
      case "safe": return safeBid;
      case "recommended": return recommendedBid;
      case "aggressive": return aggressiveBid;
      default: return 0;
    }
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#1C1917]">입찰가 분석 결과</h2>
          <p className="text-xs text-[#8C8780] mt-0.5">일반방식 (비용 공제형)</p>
        </div>
        <span className="px-2.5 py-1 text-xs font-semibold bg-[#C4956A]/10 text-[#C4956A] rounded-full">
          실시간
        </span>
      </div>

      {/* 3-Tier Bid Cards */}
      <div className="space-y-3">
        {tierConfig.map((tier) => {
          const bidValue = getBidValue(tier.key);
          const isRecommended = tier.key === "recommended";
          return (
            <motion.div
              key={tier.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const }}
              className={`rounded-xl border p-5 ${tier.bg} ${isRecommended ? "ring-2 ring-[#C4956A]/20" : ""}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg ${tier.iconBg} flex items-center justify-center`}>
                    <i className={`${tier.icon} text-white text-sm`}></i>
                  </div>
                  <div>
                    <span className="text-sm font-bold text-[#1C1917]">{tier.label}</span>
                    <span className="text-[10px] text-[#8C8780] ml-1.5 uppercase tracking-wide">{tier.sub}</span>
                  </div>
                </div>
                <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${tier.badgeBg}`}>
                  {tier.key === "safe" ? "Pmax × 0.95" : tier.key === "recommended" ? "min(Pmax, V×낙찰가율)" : "Pmax"}
                </span>
              </div>
              <p className={`text-2xl font-bold mb-2 ${bidValue < 0 ? "text-rose-500" : "text-[#1C1917]"}`}>
                {formatKRW(bidValue)}
              </p>
              {bidValue < 0 ? (
                <p className="text-xs text-rose-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  목표 수익을 낮추거나 매각 시세를 상향 조정하세요
                </p>
              ) : (
                <p className="text-xs text-[#8C8780]">{tier.description}</p>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Key Metrics Summary */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
        <h4 className="text-xs font-bold text-[#1C1917] mb-4 flex items-center gap-1.5">
          <i className="ri-bar-chart-2-line text-[#C4956A]"></i>
          핵심 지표 요약
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#FAFAF8] rounded-lg p-3">
            <p className="text-[10px] text-[#8C8780] mb-0.5">산출 상한가 (P<sub>max</sub>)</p>
            <p className={`text-sm font-bold ${pMax < 0 ? "text-rose-500" : "text-[#1C1917]"}`}>{formatKRW(pMax)}</p>
          </div>
          <div className="bg-[#FAFAF8] rounded-lg p-3">
            <p className="text-[10px] text-[#8C8780] mb-0.5">지역 낙찰가율 반영가</p>
            <p className="text-sm font-bold text-[#1C1917]">{formatKRW(winningRateAdjustedPrice)}</p>
          </div>
          <div className="bg-[#FAFAF8] rounded-lg p-3">
            <p className="text-[10px] text-[#8C8780] mb-0.5">예상 수익률</p>
            <p className={`text-sm font-bold ${estimatedROI >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
              {formatPercent(estimatedROI)}
            </p>
          </div>
          <div className="bg-[#FAFAF8] rounded-lg p-3">
            <p className="text-[10px] text-[#8C8780] mb-0.5">총 부대비용</p>
            <p className="text-sm font-bold text-[#1C1917]">{formatKRW(costs.totalCost)}</p>
          </div>
        </div>
      </div>

      {/* Detailed Cost Breakdown */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
        <h4 className="text-xs font-bold text-[#1C1917] mb-4 flex items-center gap-1.5">
          <i className="ri-file-list-3-line text-[#C4956A]"></i>
          부대비용 상세 내역
        </h4>
        <div className="space-y-0">
          {[
            { label: "인수 권리금액 (R)", value: data.takeoverAmount, info: "대항력 임차인·유치권 등" },
            { label: "수리비 (C_rep)", value: costs.repairCost, info: `면적 ${data.exclusiveArea}m² × 5만원 × 가중치 ${getAgeWeight(data.buildingAge)}` },
            { label: "명도비", value: costs.evictionCost, info: "점유자 유형별 추정" },
            { label: `취득세 (C_tax, ${(getAcquisitionTaxRate(data.homeCount, data.estimatedMarketPrice) * 100).toFixed(1)}%)`, value: costs.acquisitionTax, info: `${data.homeCount <= 1 ? "1주택 이하" : data.homeCount + "주택"}` },
            { label: "금융비용 (C_fin)", value: costs.financeCost, info: `${data.loanAmount.toLocaleString()}원 × ${data.loanRate}% × ${data.loanDuration}개월` },
            { label: "미납 관리비 (C_mgt)", value: costs.mgmtFee, info: "추정치" },
          ].map((item, i) => (
            <div key={i} className={`flex items-center justify-between py-2.5 ${i < 5 ? "border-b border-[#F0EDE8]" : ""}`}>
              <div>
                <span className="text-xs font-medium text-[#5C5650]">{item.label}</span>
                <p className="text-[10px] text-[#B8B3AE]">{item.info}</p>
              </div>
              <span className="text-xs font-semibold text-[#1C1917] whitespace-nowrap">{formatKRW(item.value)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between py-3 mt-1 border-t-2 border-[#1C1917]">
            <span className="text-sm font-bold text-[#1C1917]">합계</span>
            <span className="text-sm font-bold text-[#1C1917]">{formatKRW(costs.totalCost)}</span>
          </div>
        </div>
      </div>

      {/* Formula Reference */}
      <div className="bg-[#FAFAF8] rounded-xl border border-[#E8E5E0] p-4">
        <h4 className="text-[10px] font-bold text-[#8C8780] uppercase tracking-wide mb-2">산출 공식</h4>
        <div className="text-xs text-[#5C5650] leading-relaxed space-y-1 font-mono">
          <p>P<sub>max</sub> = V<sub>est</sub> - (R + C<sub>rep</sub> + C<sub>tax</sub> + C<sub>fin</sub> + C<sub>mgt</sub> + M<sub>target</sub>)</p>
          <p className="text-[10px] text-[#B8B3AE]">Safe = P<sub>max</sub> × 0.95 | Recommended = min(P<sub>max</sub>, V × 낙찰가율) | Aggressive = P<sub>max</sub></p>
        </div>
      </div>

      {/* AI 시뮬레이터 연결 */}
      <Link
        to="/investment-simulator"
        className="block w-full bg-gradient-to-r from-[#1C1917] to-[#3A2E28] rounded-xl p-4 hover:from-[#2A2624] hover:to-[#4A3E38] transition-all duration-300 group"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-[#C4956A]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C4956A]/30 transition-colors">
            <i className="ri-brain-line text-[#C4956A] text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white leading-snug">
              AI 시뮬레이터로 심층 분석하기
            </p>
            <p className="text-xs text-[#B8B3AE] mt-0.5">
              표면이익 함정 제거 — 실제 순이익·ROE·투자 등급까지 30초 분석
            </p>
          </div>
          <div className="flex-shrink-0">
            <i className="ri-arrow-right-line text-[#C4956A] group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
          </div>
        </div>
      </Link>
    </div>
  );
}